import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LangSwitcher from "../../../Component/LangSwitcher";
import { NAV_LINKS } from "../../Landing/landingData";

const ForgotPasswordView = ({
  forgotStep,
  forgotEmail,
  resetCode,
  newPassword,
  submitting,
  inlineError,
  onForgotEmailChange,
  onResetCodeChange,
  onNewPasswordChange,
  onForgotSubmit,
  onResetSubmit,
  onResendMail,
  onBackToRequest,
  onLoginClick
}) => {
  const { t } = useTranslation("common");
  const navbarRef = useRef(null);

  useEffect(() => {
    const navbar = navbarRef.current;
    if (!navbar) {
      return undefined;
    }

    const handleScroll = () => {
      navbar.style.boxShadow = window.scrollY > 10 ? "0 8px 32px rgba(0,0,0,0.5)" : "none";
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="forgot-auth-page">
      <div className="bg-glow"></div>

      <nav className="navbar" ref={navbarRef}>
        <div className="nav-inner">
          <Link to="/" className="logo">
            <div className="logo-icon">O</div>
            <span>
              Odoo<span className="accent">-API</span>
            </span>
          </Link>
          <div className="nav-links">
            {NAV_LINKS.filter(({ href }) => href !== "#docs").map(({ href, labelKey }) => (
              <a key={href} href={`/${href}`}>
                {t(labelKey)}
              </a>
            ))}
            <LangSwitcher className="nav-lang" />
            <Link to="/login" className="btn-nav">
              {t("Sign In")}
            </Link>
          </div>
        </div>
      </nav>

      <main className="auth-wrap">
        <div className="auth-card">
          <div className="auth-logo">
            <div className="auth-logo-icon">O</div>
            <span className="auth-logo-text">
              Odoo<span>-API</span>
            </span>
          </div>

          {forgotStep === "request" ? (
            <>
              <div className="auth-icon-wrap">🔑</div>
              <h1 className="auth-title">{t("Reset your password")}</h1>
              <p className="auth-subtitle">
                {t("Enter your email and we'll send you a verification code to reset your password.")}
              </p>

              <form onSubmit={onForgotSubmit}>
                <label className="form-label" htmlFor="forgot-email">
                  {t("Email address")}
                </label>
                <input
                  className="form-input"
                  type="email"
                  id="forgot-email"
                  name="email"
                  placeholder={t("you@company.com")}
                  value={forgotEmail}
                  onChange={(event) => onForgotEmailChange(event.target.value)}
                  required
                  autoComplete="email"
                />
                {inlineError ? <div className="forgot-inline-error">{inlineError}</div> : null}
                <button type="submit" className="btn-auth" disabled={submitting}>
                  {submitting ? t("Sending…") : t("Send Reset Code →")}
                </button>
              </form>

              <div className="auth-footer">
                {t("Remember your password?")}{" "}
                <button type="button" onClick={onLoginClick}>
                  {t("Back to sign in")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="auth-icon-wrap">✉️</div>
              <h1 className="auth-title">{t("Reset Password")}</h1>
              <p className="auth-subtitle">
                {t("Enter the verification code from your email and choose a new password.")}
              </p>

              <form onSubmit={onResetSubmit}>
                <label className="form-label" htmlFor="forgot-email-readonly">
                  {t("Email address")}
                </label>
                <input
                  className="form-input"
                  type="email"
                  id="forgot-email-readonly"
                  value={forgotEmail}
                  disabled
                />

                <label className="form-label" htmlFor="reset-code">
                  {t("Code")}
                </label>
                <input
                  className="form-input"
                  type="text"
                  id="reset-code"
                  value={resetCode}
                  onChange={(event) => onResetCodeChange(event.target.value)}
                  required
                />

                <label className="form-label" htmlFor="new-password">
                  {t("New Password")}
                </label>
                <input
                  className="form-input"
                  type="password"
                  id="new-password"
                  value={newPassword}
                  onChange={(event) => onNewPasswordChange(event.target.value)}
                  required
                  autoComplete="new-password"
                />

                {inlineError ? <div className="forgot-inline-error">{inlineError}</div> : null}

                <button type="submit" className="btn-auth" disabled={submitting}>
                  {submitting ? t("Resetting…") : t("Reset Password")}
                </button>
              </form>

              <div className="forgot-actions">
                <button type="button" className="forgot-secondary-btn" onClick={onResendMail}>
                  {t("Resend Mail")}
                </button>
                <button type="button" className="forgot-secondary-btn" onClick={onBackToRequest}>
                  {t("Back")}
                </button>
                <div className="auth-footer">
                  <button type="button" onClick={onLoginClick}>
                    ← {t("Back to sign in")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="forgot-footer">
        <span>
          © {new Date().getFullYear()} Odoo-API. {t("All rights reserved.")}
        </span>
      </footer>
    </div>
  );
};

ForgotPasswordView.propTypes = {
  forgotStep: PropTypes.oneOf(["request", "reset"]).isRequired,
  forgotEmail: PropTypes.string.isRequired,
  resetCode: PropTypes.string.isRequired,
  newPassword: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  inlineError: PropTypes.string,
  onForgotEmailChange: PropTypes.func.isRequired,
  onResetCodeChange: PropTypes.func.isRequired,
  onNewPasswordChange: PropTypes.func.isRequired,
  onForgotSubmit: PropTypes.func.isRequired,
  onResetSubmit: PropTypes.func.isRequired,
  onResendMail: PropTypes.func.isRequired,
  onBackToRequest: PropTypes.func.isRequired,
  onLoginClick: PropTypes.func.isRequired
};

export default ForgotPasswordView;
